import { useState, forwardRef, useImperativeHandle } from 'react';
import { Modal, Form } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { formatDateTime } from '@/shared/utils/formatDate';
import LoadingButton from '@/components/ui/LoadingButton';
import { getDefaultSelectableIdol } from '@/shared/store/slices/idolSlice';

const CreateTokenModal = forwardRef(({ onSubmit, onConfirm }, ref) => {
  const { t } = useTranslation();
  const idolList = useSelector(state => state.idol.list);
  const currentIdolId = useSelector(state => state.user.idolId);
  const [show, setShow] = useState(false);
  const [step, setStep] = useState('create'); // 'create' 或 'success'
  const [formData, setFormData] = useState({
    selectedIdol: '',
    tokenName: '',
    validityPeriod: '7_days'
  });
  const [tokenResult, setTokenResult] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // 預設選當前使用中的偶像；不在清單中則選第一個（皆跳過停權偶像），沒有可選的偶像時維持未選
  const getInitialFormData = () => {
    const defaultIdol = getDefaultSelectableIdol(idolList, currentIdolId);
    return {
      selectedIdol: defaultIdol ? defaultIdol.idol_id : '',
      tokenName: '',
      validityPeriod: '7_days'
    };
  };

  const handleClose = () => {
    setShow(false);
    setStep('create');
    setFormData(getInitialFormData());
    setTokenResult(null);
  };

  useImperativeHandle(ref, () => ({
    show: () => {
      setStep('create');
      setFormData(getInitialFormData());
      setShow(true);
    },
    hide: handleClose,
    showSuccess: (data) => {
      setTokenResult(data);
      setStep('success');
      setShow(true);
    }
  }));

  // 送出後鎖住表單直到 onSubmit 完成：成功時父層會切到 success step，
  // 失敗時維持在表單上（父層另開 StatusModal），使用者不必重打
  const handleSubmit = async () => {
    const submitData = {
      name: formData.tokenName,
      idol_id: formData.selectedIdol,
      expire_days: formData.validityPeriod === '7_days' ? 7 :
                  formData.validityPeriod === '30_days' ? 30 :
                  formData.validityPeriod === '90_days' ? 90 :
                  formData.validityPeriod === 'permanent' ? -1 : 7
    };
    setIsSubmitting(true);
    try {
      await onSubmit(submitData);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConfirm = () => {
    handleClose();
    if (onConfirm) {
      onConfirm();
    }
  };

  const handleCopy = async () => {
    if (tokenResult?.token) {
      try {
        await navigator.clipboard.writeText(tokenResult.token);
      } catch (err) {
        console.error('複製失敗:', err);
      }
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // 創建表單視圖
  const renderCreateForm = () => (
    <>
      <Modal.Header className="border-bottom pb-3" closeButton={!isSubmitting}>
        <Modal.Title className="fw-bold">{t('create_token')}</Modal.Title>
      </Modal.Header>

      <Modal.Body className="py-4">
        {/* 選擇偶像 */}
        <div className="mb-4">
          <Form.Label className="fw-bold">{t('please_select_idol')}</Form.Label>
          <Form.Select
            value={formData.selectedIdol}
            // option 的 value 是字串，轉回數字，與預設選取（idol_id）同型別（同 IdolSelector）
            onChange={(e) => handleInputChange('selectedIdol', Number(e.target.value))}
          >
            {idolList.map(idol => (
              <option key={idol.idol_id} value={idol.idol_id} disabled={idol.idol_is_suspended}>
                {idol.idol_is_suspended
                  ? t('idol_option_suspended', { name: idol.idol_nick_name })
                  : idol.idol_nick_name}
              </option>
            ))}
          </Form.Select>
        </div>

        {/* Token 名稱 */}
        <div className="mb-4">
          <Form.Label className="fw-bold">{t('token_name')}</Form.Label>
          <Form.Control
            type="text"
            placeholder={t('token_name')}
            value={formData.tokenName}
            onChange={(e) => handleInputChange('tokenName', e.target.value)}
          />
        </div>

        {/* 有效期限 */}
        <div>
          <Form.Label className="fw-bold">{t('validity_period')}</Form.Label>
          <div className="d-flex flex-wrap gap-3">
            <Form.Check
              type="radio"
              name="validityPeriod"
              id="7_days"
              label={t('7_days')}
              checked={formData.validityPeriod === '7_days'}
              onChange={() => handleInputChange('validityPeriod', '7_days')}
            />
            <Form.Check
              type="radio"
              name="validityPeriod"
              id="30_days"
              label={t('30_days')}
              checked={formData.validityPeriod === '30_days'}
              onChange={() => handleInputChange('validityPeriod', '30_days')}
            />
            <Form.Check
              type="radio"
              name="validityPeriod"
              id="90_days"
              label={t('90_days')}
              checked={formData.validityPeriod === '90_days'}
              onChange={() => handleInputChange('validityPeriod', '90_days')}
            />
            <Form.Check
              type="radio"
              name="validityPeriod"
              id="permanent"
              label={t('permanent')}
              checked={formData.validityPeriod === 'permanent'}
              onChange={() => handleInputChange('validityPeriod', 'permanent')}
            />
          </div>
        </div>
      </Modal.Body>

      <Modal.Footer className="border-0 d-flex justify-content-end gap-2">
        <button
          className="btn btn-neutral rounded-pill"
          onClick={handleClose}
          disabled={isSubmitting}
        >
          {t('cancel')}
        </button>
        <LoadingButton
          className="btn btn-primary rounded-pill"
          onClick={handleSubmit}
          disabled={!formData.tokenName.trim()}
          isLoading={isSubmitting}
        >
          {t('confirm')}
        </LoadingButton>
      </Modal.Footer>
    </>
  );

  // 成功結果視圖
  const renderSuccessResult = () => (
    <>
      <Modal.Header className="border-0 pb-0" closeButton>
      </Modal.Header>

      <Modal.Body className="text-center pt-0">
        {/* 成功圖示 */}
        <div className="mb-4">
          <div className="">
            <FontAwesomeIcon icon={["fas", "fa-circle-check"]} className="text-primary" size="3x" />
          </div>
        </div>

        {/* 標題 */}
        <h4 className="mb-2 fw-bold">{t('token_created_successfully')}</h4>

        {/* 提示文字 */}
        <p className="text-danger fw-bolder mb-4">{t('copy_and_save_token')}</p>

        {/* Token 區塊 */}
        <div className="bg-light rounded p-3 mb-4 text-start">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <span className="fw-bolder">API Token</span>
            <button
              className="btn btn-outline-primaryF btn-sm rounded-pill"
              onClick={handleCopy}
            >
              {t('copy')}
            </button>
          </div>

          <div className="bg-white rounded p-3 mb-3 text-break">
            <small className="line-clamp-2">{tokenResult?.token}</small>
          </div>

          <div className="fw-bolder">
            {t('valid_until')}{tokenResult?.expiryDate ? formatDateTime(tokenResult.expiryDate) : t('permanent')}
          </div>
        </div>

        {/* 警告訊息 */}
        <div className="text-start">
          <div className="d-flex align-items-start mb-2">
            <FontAwesomeIcon icon={["fas", "triangle-exclamation"]} className="text-danger me-2 mt-1" />
            <small className="">{t('token_warning_1')}</small>
          </div>
          <div className="d-flex align-items-start">
            <FontAwesomeIcon icon={["fas", "triangle-exclamation"]} className="text-danger me-2 mt-1" />
            <small className="">{t('token_warning_2')}</small>
          </div>
        </div>
      </Modal.Body>

      <Modal.Footer className="border-0 justify-content-end">
        <button
          className="btn btn-primary rounded-pill px-4"
          onClick={handleConfirm}
        >
          {t('confirm')}
        </button>
      </Modal.Footer>
    </>
  );

  return (
    <Modal
      show={show}
      onHide={handleClose}
      centered
      backdrop={isSubmitting ? 'static' : true}
      keyboard={!isSubmitting}
    >
      {step === 'create' ? renderCreateForm() : renderSuccessResult()}
    </Modal>
  );
});

CreateTokenModal.displayName = 'CreateTokenModal';

export default CreateTokenModal;